import { generateRandomId } from '@webng/uniqueid-generator';
import { useCallback, useState } from 'react';

const REACTIONS_DATA_KEY = 'tickaroo-reactions-data';

export type ReactionsData = {
  [liveblogId: string]: {
    userId: string;
    reactions: {
      [eventId: string]: {
        [reactionId: string]: boolean;
      }
    }
  }
}

function createReactionsData(liveblogId: string, eventId?: string): ReactionsData[string] {
  return {
    userId: generateRandomId("tickaroo-reactions-user-id"),
    reactions: eventId ? {
      [eventId]: {}
    } : {}
  }
}

function initializeReactionsData(liveblogId: string, eventId?: string): ReactionsData | undefined {
  if(typeof window === 'undefined' || window?.localStorage === undefined) return undefined;
  const data = localStorage.getItem(REACTIONS_DATA_KEY);
  if(data) {
    const parsedData: ReactionsData = JSON.parse(data);
    if(parsedData[liveblogId]) {
      if(eventId && !parsedData[liveblogId].reactions[eventId]) {
        const newData: ReactionsData = {
          ...parsedData,
          [liveblogId]: {
            ...parsedData[liveblogId],
            reactions: {
              ...parsedData[liveblogId].reactions,
              [eventId]: {}
            }
          }
        }
        localStorage.setItem(REACTIONS_DATA_KEY, JSON.stringify(newData));
        return newData;
      } else {
        return parsedData;
      }
    } else {
      const newData = {
        [liveblogId]: createReactionsData(liveblogId, eventId)
      }
      localStorage.setItem(REACTIONS_DATA_KEY, JSON.stringify(newData));
      return newData;
    }
  } else {
    const newData = {
      [liveblogId]: createReactionsData(liveblogId, eventId)
    }
    localStorage.setItem(REACTIONS_DATA_KEY, JSON.stringify(newData));
    return newData;
  }
}

export function useReactionsData(liveblogId: string) {
  const [reactionsData, setReactionsData] = useState<ReactionsData | undefined>(() => initializeReactionsData(liveblogId));

  const addUserReaction = useCallback((eventId: string, reactionId: string) => {
    setReactionsData((prev) => {
      const newData = { ...prev };
      newData[liveblogId].reactions[eventId][reactionId] = true;
      localStorage.setItem(REACTIONS_DATA_KEY, JSON.stringify(newData));
      return newData;
    });
  }, [liveblogId]);

  const removeUserReaction = useCallback((eventId: string, reactionId: string) => {
    setReactionsData((prev) => {
      const newData = { ...prev };
      delete newData[liveblogId].reactions[eventId][reactionId];
      localStorage.setItem(REACTIONS_DATA_KEY, JSON.stringify(newData));
      return newData;
    });
  }, [liveblogId]);

  return reactionsData ? {
    userId: reactionsData[liveblogId].userId,
    userReactions: reactionsData[liveblogId].reactions,
    addUserReaction,
    removeUserReaction,
  } : undefined;
}

export function useReactionsDataForEvent(liveblogId: string, eventId: string) {
  const [reactionsData, setReactionsData] = useState<ReactionsData | undefined>(() => initializeReactionsData(liveblogId, eventId));

  const addUserReaction = useCallback((reactionId: string) => {
    setReactionsData((prev) => {
      const newData = { ...prev };
      newData[liveblogId].reactions[eventId][reactionId] = true;
      localStorage.setItem(REACTIONS_DATA_KEY, JSON.stringify(newData));
      return newData;
    });
  }, [liveblogId, eventId]);

  const removeUserReaction = useCallback((reactionId: string) => {
    setReactionsData((prev) => {
      const newData = { ...prev };
      delete newData[liveblogId].reactions[eventId][reactionId];
      localStorage.setItem(REACTIONS_DATA_KEY, JSON.stringify(newData));
      return newData;
    });
  }, [liveblogId, eventId]);

  return reactionsData ? {
    userId: reactionsData[liveblogId].userId,
    userReactions: reactionsData[liveblogId].reactions[eventId],
    addUserReaction,
    removeUserReaction,
  } : undefined;
}